import { useNavigation } from "../../../hooks/useNavigation";
import { useCallback, useState } from "react";
import { authActions } from "../../../ducks/auth";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { AppConfig } from "../../../AppConfig";
import { PageRoute } from "../../../constants/routes";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";

export interface LoginError {
  type: 'email' | 'password' | 'general';
  message: string;
}

// Helper function to safely access error data
const getErrorData = (error: any) => {
  try {
    return error?.response?.data || {};
  } catch {
    return {};
  }
};

// Helper function to safely get error message
const getErrorMessage = (errorData: any, field: string): string | null => {
  try {
    const fieldData = errorData[field];
    if (Array.isArray(fieldData)) {
      return fieldData[0] || null;
    }
    return fieldData || null;
  } catch {
    return null;
  }
};

export const useLoginPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = useCallback((email: string, password: string, onError?: (error: LoginError) => void) => {
    setIsLoading(true);

    dispatch(
      authActions.login.started({
        email,
        password,
        onSuccess: (response) => {
          setIsLoading(false);
          LocalStorageManager.Instance.saveObject(
            AppConfig.Instance.LocalStorageKey["accessToken"],
            response.access,
          );
          dispatch(authActions.setIsLoggedIn(true));

          navigateToPage(PageRoute.HOME);
        },
        onError: (error) => {
          setIsLoading(false);
          console.error("Login failed:", error);

          // Handle different error scenarios
          let loginError: LoginError;
          const errorData = getErrorData(error);
          const status = error?.response?.status;

          if (status === 401) {
            // Check for specific field errors first
            const emailError = getErrorMessage(errorData, 'email');
            const passwordError = getErrorMessage(errorData, 'password');

            if (emailError) {
              loginError = {
                type: 'email',
                message: emailError
              };
            } else if (passwordError) {
              loginError = {
                type: 'password',
                message: passwordError
              };
            } else {
              // Generic authentication error
              loginError = {
                type: 'password',
                message: 'Invalid email or password. Please check your credentials and try again.'
              };
            }
          } else if (status === 400) {
            // Bad request - could be validation errors
            const emailError = getErrorMessage(errorData, 'email');
            const passwordError = getErrorMessage(errorData, 'password');

            if (emailError) {
              loginError = {
                type: 'email',
                message: emailError
              };
            } else if (passwordError) {
              loginError = {
                type: 'password',
                message: passwordError
              };
            } else {
              loginError = {
                type: 'general',
                message: 'Please check your input and try again.'
              };
            }
          } else if (status === 404) {
            loginError = {
              type: 'email',
              message: 'Email address not found. Please check your email or contact admin.'
            };
          } else if (status && status >= 500) {
            loginError = {
              type: 'general',
              message: 'Server error. Please try again later or contact admin.'
            };
          } else if (error?.code === 'NETWORK_ERROR' ||
            error?.message?.includes('Network Error') ||
            error?.code === 'ECONNABORTED' ||
            error?.message?.includes('timeout')) {
            loginError = {
              type: 'general',
              message: 'Network error. Please check your internet connection and try again.'
            };
          } else if (status === 0) {
            // CORS or network connectivity issues
            loginError = {
              type: 'general',
              message: 'Unable to connect to the server. Please check your internet connection.'
            };
          } else {
            loginError = {
              type: 'general',
              message: 'An unexpected error occurred. Please try again.'
            };
          }

          onError?.(loginError);
        },
      }),
    );
  }, []);

  return { handleLogin, isLoading };
};
